// @ts-nocheck
import React, { useEffect, useState } from "react";
import SharedSelect from "../components/SharedSelect";
import { useNavigate } from "react-router-dom";
import { getUser } from "../auth/auth";
import TopNav from "../components/TopNav";
import LocationPickerModal from "../components/LocationPickerModal";
import { useToast } from "../components/ToastProvider";
import { createTrade } from "../api/trades";
import { getMonetizationMe } from "../api/monetization";
import { getCardMarket } from "../api/cards";
import "../styles/PostBounty.css"; // Use PostBounty styles for shared layout
import { buildCardDetailUrl } from "../utils/marketAnalytics";
import CardAutocompleteInput from "../components/CardAutocompleteInput";
import CardScanPanel from "../components/CardScanPanel";
import "../styles/CardAutocomplete.css";
import { canonicalGameToDisplayLabel } from "../utils/canonicalGames";
import {
  deleteDraftImage,
  getDraftImage,
  saveDraftImage,
} from "../utils/draftImageStore";
import {
  TRADE_DRAFT_TTL_MS,
  TRADE_DRAFT_VERSION,
  getPostTradeDraftKeys,
  readPostTradeDraft,
  writePostTradeDraft,
} from "../utils/postTradeDraft";
import { formatLocationLabel, getProfileLocation } from "../utils/location";

const TRADE_DEFAULT_FORM = {
  cardName: "",
  game: "Magic: The Gathering",
  condition: "Near Mint",
  gradingCompany: "raw",
  gradeNumber: "",
  photoUrl: "",
  zipCode: "",
  radius: 25,
  meetupPreference: "public",
};

function PostTrade() {
  const navigate = useNavigate();
  const user = getUser();
  const profileLocation = getProfileLocation(user);
  const { showToast } = useToast();
  const userId = user?.id ?? null;
  const { storageKey: draftStorageKey, imageKey: draftImageKey } = getPostTradeDraftKeys(userId);

  const [formData, setFormData] = useState(TRADE_DEFAULT_FORM);
  const [photoFile, setPhotoFile] = useState(null);
  const [selectedCard, setSelectedCard] = useState(null);
  const [marketHint, setMarketHint] = useState("");
  const [photoError, setPhotoError] = useState("");
  const [draftHydrated, setDraftHydrated] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(profileLocation);
  const [locationModalOpen, setLocationModalOpen] = useState(false);

  const [loading, setLoading] = useState(false);
  const [tradeLimit, setTradeLimit] = useState<number | null>(null);
  const [activeTrades, setActiveTrades] = useState<number>(0);
  const isOnePieceGame = formData.game.trim().toLowerCase() === "one piece";
  const sourceContext = String(formData?.sourceContext || "").trim().toLowerCase();
  const isCollectionSource = sourceContext === "collection";
  const collectionFrontImageUrl = String(formData?.frontImageUrl || "").trim();
  const collectionBackImageUrl = String(formData?.backImageUrl || "").trim();
  const effectiveFrontPreviewUrl = String(formData?.photoUrl || "").trim() || collectionFrontImageUrl;

  useEffect(() => {
    if (!userId) return;
    getMonetizationMe()
      .then((data) => {
        setTradeLimit(Number(data?.plan?.maxActiveTrades || 2));
        setActiveTrades(Number(data?.usage?.activeTrades || 0));
      })
      .catch(() => undefined);
  }, [userId]);

  useEffect(() => {
    let cancelled = false;

    const restoreDraft = async () => {
      const parsed = readPostTradeDraft(draftStorageKey);
      if (parsed) {
        const isExpired = !parsed?.updatedAt || Date.now() - parsed.updatedAt > TRADE_DRAFT_TTL_MS;

        if (parsed?.version === TRADE_DRAFT_VERSION && !isExpired && parsed?.formData) {
          setFormData((prev) => ({ ...prev, ...parsed.formData, photoUrl: "" }));
          setSelectedCard(parsed?.selectedCard || null);
          if (parsed?.formData?.zipCode) {
            setSelectedLocation({
              postalCode: parsed.formData.zipCode,
              countryCode: "US",
              label: parsed.formData.zipCode,
              latitude: null,
              longitude: null,
            });
          }
        } else {
          sessionStorage.removeItem(draftStorageKey);
          await deleteDraftImage(draftImageKey).catch(() => undefined);
        }
      }

      try {
        const imageRecord = await getDraftImage(draftImageKey);
        if (!cancelled && imageRecord) {
          const isExpired = Date.now() - imageRecord.updatedAt > TRADE_DRAFT_TTL_MS;
          if (isExpired) {
            await deleteDraftImage(draftImageKey).catch(() => undefined);
          } else {
            const restoredFile = new File(
              [imageRecord.blob],
              imageRecord.meta.name || "trade-image.webp",
              {
                type: imageRecord.meta.type || "image/webp",
                lastModified: imageRecord.meta.lastModified || Date.now(),
              },
            );
            setPhotoFile(restoredFile);
            const restoredPreview = URL.createObjectURL(restoredFile);
            setFormData((prev) => {
              if (prev.photoUrl?.startsWith("blob:")) {
                URL.revokeObjectURL(prev.photoUrl);
              }
              return { ...prev, photoUrl: restoredPreview };
            });
          }
        } else if (!cancelled) {
          setFormData((prev) => {
            const nextSourceContext = String(prev?.sourceContext || "").trim().toLowerCase();
            const nextFrontImageUrl = String(prev?.frontImageUrl || "").trim();
            if (nextSourceContext !== "collection" || !nextFrontImageUrl) return prev;
            return {
              ...prev,
              photoUrl: prev?.photoUrl || nextFrontImageUrl,
            };
          });
        }
      } catch {
        if (!cancelled) {
          setPhotoError("Draft text restored. Please reselect your image.");
        }
      } finally {
        if (!cancelled) {
          setDraftHydrated(true);
        }
      }
    };

    void restoreDraft();

    return () => {
      cancelled = true;
    };
  }, [draftStorageKey, draftImageKey]);

  useEffect(() => {
    if (!draftHydrated && profileLocation?.postalCode) {
      setFormData((prev) => ({ ...prev, zipCode: prev.zipCode || profileLocation.postalCode }));
      setSelectedLocation(profileLocation);
    }
  }, [draftHydrated, profileLocation]);

  useEffect(() => {
    if (!draftHydrated) return;
    const timeoutId = setTimeout(() => {
      try {
        writePostTradeDraft(draftStorageKey, formData, selectedCard);
      } catch (error) {
        console.warn("Failed to persist trade draft.", error);
      }
    }, 250);

    return () => clearTimeout(timeoutId);
  }, [draftHydrated, draftStorageKey, formData, selectedCard]);

  useEffect(() => {
    const scroller = document.querySelector(".app-scroll-root");
    if (scroller) {
      scroller.scrollTo({ top: 0, behavior: "auto" });
    } else {
      window.scrollTo({ top: 0, behavior: "auto" });
    }
  }, []);

  useEffect(() => {
    return () => {
      if (formData.photoUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(formData.photoUrl);
      }
    };
  }, [formData.photoUrl]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "game") {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
      setSelectedCard((prevSelectedCard) => {
        if (!prevSelectedCard) return null;
        const selectedCardDisplayGame = canonicalGameToDisplayLabel(prevSelectedCard.game);
        return selectedCardDisplayGame === value ? prevSelectedCard : null;
      });
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const clearDraftState = async () => {
    sessionStorage.removeItem(draftStorageKey);
    await deleteDraftImage(draftImageKey).catch(() => undefined);
  };

  const handleCancel = async () => {
    if (formData.photoUrl?.startsWith("blob:")) {
      URL.revokeObjectURL(formData.photoUrl);
    }
    setFormData(TRADE_DEFAULT_FORM);
    setPhotoFile(null);
    setSelectedCard(null);
    setMarketHint("");
    setPhotoError("");
    await clearDraftState();
    navigate(-1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) {
      showToast("Please log in to post a trade", "warning");
      navigate("/login");
      return;
    }

    if (tradeLimit !== null && activeTrades >= tradeLimit) {
      showToast(`Plan limit reached: your plan allows ${tradeLimit} active trades.`, "warning");
      return;
    }

    if (!formData.cardName) {
      showToast("Please fill in all required fields", "warning");
      return;
    }
    if (!selectedLocation && !formData.zipCode) {
      showToast("Set where this trade is currently available before posting.", "warning");
      return;
    }
    if (!isOnePieceGame && !selectedCard) {
      showToast("Please select a valid card from suggestions", "warning");
      return;
    }

    setLoading(true);

    try {
      let uploadFile = photoFile;
      if (!uploadFile && isCollectionSource && collectionFrontImageUrl) {
        const response = await fetch(collectionFrontImageUrl);
        if (!response.ok) {
          throw new Error("Could not load the saved collection photo for this trade.");
        }
        const blob = await response.blob();
        if (!blob || !blob.size) {
          throw new Error("Saved collection photo is unavailable. Please rescan this card.");
        }
        uploadFile = new File(
          [blob],
          `${String(formData.cardName || "trade-image").trim().replace(/[^a-z0-9-_]+/gi, "-") || "trade-image"}.webp`,
          {
            type: blob.type || "image/webp",
            lastModified: Date.now(),
          },
        );
      }

      if (!uploadFile) {
        showToast(
          isCollectionSource
            ? "No collection front image is available. Please upload a front image to continue."
            : "Please upload front and back images, then confirm the scan.",
          "warning",
        );
        return;
      }

      const payload = new FormData();
      payload.append("userId", String(user.id));
      payload.append("cardName", String(formData.cardName));
      payload.append("game", String(formData.game));
      if (selectedCard) {
        payload.append("cardGame", String(selectedCard.game));
        payload.append("cardId", String(selectedCard.cardId));
        payload.append("cardNameCanonical", String(selectedCard.cardName));
      }
      payload.append("condition", String(formData.condition));
      payload.append("gradingCompany", String(formData.gradingCompany || "raw"));
      payload.append("gradeNumber", String(formData.gradeNumber || ""));
      if (formData.zipCode) {
        payload.append("zipCode", String(formData.zipCode));
      }
      payload.append("radius", String(parseInt(formData.radius, 10) || 25));
      payload.append("meetupPreference", String(formData.meetupPreference || "public"));
      payload.append("file", uploadFile);

      await createTrade(payload);
      void clearDraftState().catch((draftError) => {
        console.warn("Trade was created but draft cleanup failed.", draftError);
      });

      showToast("Trade posted successfully! We'll notify you of any matches.", "success");
      navigate("/local-trades", { replace: true });
    } catch (error) {
      console.error("Error posting trade:", error);
      showToast(error?.message || "Failed to post trade. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let active = true;
    const loadMarket = async () => {
      if (isOnePieceGame) {
        setMarketHint("Price unavailable");
        return;
      }
      if (!selectedCard?.cardId || !selectedCard?.game) {
        setMarketHint("");
        return;
      }
      try {
        const data = await getCardMarket({ game: selectedCard.game, cardId: selectedCard.cardId });
        if (!active) return;
        const low = data?.market?.low;
        const high = data?.market?.high;
        if (low != null && high != null) {
          setMarketHint(`Market range: $${low}-$${high}`);
        } else {
          setMarketHint("Price unavailable");
        }
      } catch {
        if (active) setMarketHint("Price unavailable");
      }
    };
    void loadMarket();
    return () => {
      active = false;
    };
  }, [selectedCard, isOnePieceGame]);

  return (
    <>
      <TopNav />
      <div className="post-bounty-page page-with-top-nav">
        <div className="bounty-container">
          <div className="bounty-header">
            <button className="back-btn" onClick={() => navigate(-1)}>
              ←
            </button>
            <div>
              <h1>Post a Trade</h1>
              <p className="progress-text">Trade posts only contain your card.</p>
            </div>
          </div>

          <div className="bounty-card">
            <form className="bounty-form" onSubmit={handleSubmit}>
              {/* Card Details Section */}
              <div className="form-section card-panel">
                <h2 className="section-title">CARD DETAILS</h2>
                <div className="section-divider"></div>
                
                <div className="info-box game-tooltip">
                  <h3>Trade Photo</h3>
                  <p>
                    {isCollectionSource
                      ? "This collection card's front image will be used as your trade photo. You can rescan to replace it."
                      : "The front scan image will be used as your trade photo everywhere after you confirm the card."}
                  </p>
                  {photoError && (
                    <p className="dropzone-helper" style={{ color: "#ff8f8f", marginTop: "8px" }}>
                      {photoError}
                    </p>
                  )}
                </div>
                <CardScanPanel
                  flowContext="trade"
                  gameHint={formData.game}
                  title="Scan Card Identity"
                  subtitle={
                    isCollectionSource
                      ? "Collection card details are prefilled. Rescan only if you want to replace the current trade photo."
                      : "Upload front and back images to detect the card first. Your front image will be used as the trade photo everywhere."
                  }
                  externalFrontPreview={effectiveFrontPreviewUrl}
                  externalBackPreview={isCollectionSource ? collectionBackImageUrl : ""}
                  onFilesChange={({ frontFile }) => {
                    if (!frontFile) {
                      setPhotoError("");
                      setPhotoFile(null);
                      setFormData((prev) => {
                        if (prev.photoUrl?.startsWith("blob:")) {
                          URL.revokeObjectURL(prev.photoUrl);
                        }
                        return {
                          ...prev,
                          photoUrl:
                            String(prev?.sourceContext || "").trim().toLowerCase() === "collection"
                              ? String(prev?.frontImageUrl || "").trim()
                              : "",
                        };
                      });
                      void deleteDraftImage(draftImageKey).catch(() => undefined);
                      return;
                    }

                    setPhotoError("");
                    setPhotoFile(frontFile);
                    setFormData((prev) => {
                      if (prev.photoUrl?.startsWith("blob:")) {
                        URL.revokeObjectURL(prev.photoUrl);
                      }
                      return { ...prev, photoUrl: URL.createObjectURL(frontFile) };
                    });
                    void saveDraftImage(draftImageKey, frontFile, {
                      name: frontFile.name || "trade-image.webp",
                      type: frontFile.type || "image/webp",
                      lastModified: frontFile.lastModified || Date.now(),
                    }).catch(() => {
                      setPhotoError("Could not save front image draft. If you leave this page, reselect the scan photos.");
                    });
                  }}
                  onConfirmed={(confirmedCard) => {
                    setSelectedCard(confirmedCard);
                    setFormData((prev) => ({
                      ...prev,
                      cardName: confirmedCard?.cardName || prev.cardName,
                      game: canonicalGameToDisplayLabel(confirmedCard?.game) || prev.game,
                    }));
                  }}
                />

                <div className="form-group full-width">
                  <label>Card Name *</label>
                  <div className="input-with-feedback">
                    <CardAutocompleteInput
                      id="tradeCardName"
                      game={formData.game}
                      value={formData.cardName}
                      onValueChange={(nextValue) =>
                        setFormData((prev) => ({ ...prev, cardName: nextValue }))
                      }
                      selectedCard={selectedCard}
                      onSelectCard={setSelectedCard}
                      placeholder="Search and select card"
                      required
                    />
                    {selectedCard && <span className="check-icon">✓</span>}
                  </div>
                  {isOnePieceGame && (
                    <p className="dropzone-helper">One Piece canonical validation coming soon.</p>
                  )}
                  {selectedCard && (
                    <button
                      type="button"
                      className="market-hint-inline"
                      onClick={() =>
                        navigate(
                          buildCardDetailUrl(
                            selectedCard.cardId,
                            selectedCard.cardName,
                            selectedCard.game
                          )
                        )
                      }
                    >
                      {marketHint || "Loading market data..."} · View card details
                    </button>
                  )}
                </div>

                <div className="form-row-2col">
                  <div className="form-group">
                    <label>Game *</label>
                    <SharedSelect name="game" value={formData.game} onChange={handleChange}>
                      <option>Magic: The Gathering</option>
                      <option>Pokémon</option>
                      <option>Yu-Gi-Oh!</option>
                      <option>One Piece</option>
                    </SharedSelect>
                  </div>

                  <div className="form-group">
                    <label>Condition *</label>
                    <SharedSelect name="condition" value={formData.condition} onChange={handleChange} disabled={formData.gradingCompany !== "raw"}>
                      <option>Near Mint</option>
                      <option>Lightly Played</option>
                      <option>Moderately Played</option>
                      <option>Heavily Played</option>
                      <option>Damaged</option>
                    </SharedSelect>
                  </div>
                </div>

                <div className="form-row-2col">
                  <div className="form-group">
                    <label>Grading Company</label>
                    <SharedSelect name="gradingCompany" value={formData.gradingCompany} onChange={handleChange}>
                      <option value="raw">Raw (Ungraded)</option>
                      <option value="psa">PSA</option>
                      <option value="bgs">BGS</option>
                      <option value="cgc">CGC</option>
                      <option value="tag">TAG</option>
                      <option value="sgc">SGC</option>
                      <option value="other">Other</option>
                    </SharedSelect>
                  </div>

                  <div className="form-group">
                    <label>Grade Number</label>
                    <SharedSelect
                      name="gradeNumber"
                      value={formData.gradeNumber}
                      onChange={handleChange}
                      disabled={formData.gradingCompany === "raw"}
                    >
                      <option value="">Any</option>
                      <option value="10">10</option>
                      <option value="9.5">9.5</option>
                      <option value="9">9</option>
                      <option value="8.5">8.5</option>
                      <option value="8">8</option>
                      <option value="7.5">7.5</option>
                      <option value="7">7</option>
                      <option value="6">6</option>
                      <option value="5">5</option>
                      <option value="4">4</option>
                      <option value="3">3</option>
                      <option value="2">2</option>
                      <option value="1">1</option>
                    </SharedSelect>
                  </div>
                </div>
              </div>

              {/* Location & Meetup Section */}
              <div className="form-section card-panel">
                <h2 className="section-title">LOCATION & MEETUP</h2>
                <div className="section-divider"></div>
                
                <div className="form-row-2col">
                  <div className="form-group">
                    <label>Trade Location *</label>
                    <div className="location-value" style={{ marginBottom: "10px" }}>
                      {formatLocationLabel(selectedLocation, "No trade location selected")}
                    </div>
                    <div className="location-actions" style={{ marginBottom: "12px" }}>
                      <button type="button" className="btn-cancel" onClick={() => setLocationModalOpen(true)}>
                        {selectedLocation ? "Change location" : "Set location"}
                      </button>
                      {profileLocation ? (
                        <button
                          type="button"
                          className="btn-save"
                          onClick={() => {
                            setSelectedLocation(profileLocation);
                            setFormData((prev) => ({ ...prev, zipCode: profileLocation.postalCode }));
                          }}
                        >
                          Use profile location
                        </button>
                      ) : null}
                    </div>
                    <label>Your Postal Code *</label>
                    <div className="input-with-feedback">
                      <input
                        type="text"
                        name="zipCode"
                        value={formData.zipCode}
                        onChange={(e) => {
                          const value = e.target.value.slice(0, 16);
                          setFormData(prev => ({ ...prev, zipCode: value }));
                          setSelectedLocation((prev) =>
                            prev && prev.postalCode === value
                              ? prev
                              : value
                                ? {
                                    postalCode: value,
                                    countryCode: prev?.countryCode || "US",
                                    label: prev?.postalCode === value ? prev.label : value,
                                    latitude: prev?.postalCode === value ? prev.latitude : null,
                                    longitude: prev?.postalCode === value ? prev.longitude : null,
                                  }
                                : null,
                          );
                        }}
                        placeholder="e.g., 10001 / SW1A 1AA"
                        maxLength="16"
                      />
                      {formData.zipCode.trim().length >= 3 && <span className="check-icon">✓</span>}
                    </div>
                    {formData.zipCode.trim().length >= 3 && <span className="success-text">Only post cards that are currently available in this area.</span>}
                  </div>

                  <div className="form-group">
                    <label>Search Radius</label>
                    <SharedSelect name="radius" value={formData.radius} onChange={handleChange}>
                      <option value={10}>10 miles</option>
                      <option value={25}>25 miles</option>
                      <option value={50}>50 miles</option>
                      <option value={100}>100 miles</option>
                    </SharedSelect>
                  </div>
                </div>
              </div>

              {/* Safety Guidelines Section */}
              <div className="form-section card-panel">
                <h2 className="section-title">SAFETY GUIDELINES</h2>
                <div className="section-divider"></div>
                
                <ul className="safety-list">
                  <li>Always meet in public places during daylight hours</li>
                  <li>Bring a friend if trading expensive cards</li>
                  <li>Inspect cards carefully before completing trade</li>
                  <li>Never share your address until trade is confirmed</li>
                  <li>Report suspicious behavior immediately</li>
                </ul>
              </div>

              {/* How Trades Work */}
              <div className="info-box game-tooltip">
                <h3>💡 How Trade Matching Works</h3>
                <ul>
                  <li>🎯 Your card is quietly matched with collectors nearby who are actively looking for it</li>
                  <li>🔔 When someone posts a matching bounty, you'll be notified</li>
                  <li>📋 You can review their request and decide whether to make an offer</li>
                  <li>👤 You can also receive offers from users who view your profile</li>
                  <li>🤝 Once you agree, arrange a safe public meetup to complete the trade</li>
                </ul>
              </div>

              {/* Form Actions */}
              <div className="decision-moment">
                <p className="decision-text">You're about to post your card for trade.</p>
              </div>
              <div className="form-footer">
                <button type="button" className="btn-cancel" onClick={() => void handleCancel()}>
                  Cancel
                </button>
                <button type="submit" className="btn-submit" disabled={loading}>
                  {loading ? "Posting..." : "Post Trade"}
                </button>
              </div>
              <p className="trust-reassurance">
                🔒 No personal information is shared on Cardex.
              </p>
            </form>
          </div>
        </div>
      </div>
      <LocationPickerModal
        isOpen={locationModalOpen}
        title="Trade Location"
        subtitle="Set where these cards are currently on your person and available to trade."
        confirmLabel="Use this trade location"
        initialLocation={selectedLocation || profileLocation}
        onClose={() => setLocationModalOpen(false)}
        onConfirm={async (location) => {
          setSelectedLocation(location);
          setFormData((prev) => ({ ...prev, zipCode: location.postalCode }));
        }}
      />
    </>
  );
}

export default PostTrade;
